import { AbstractNetworkPrintingAdapter } from './AbstractNetworkPrintingAdapter';
import { INetworkPrintingConfig, IGetPrinterDPIConfig, IGetPrinterDPIResult, NetworkPrintingValidation, DEFAULT_NETWORK_PRINTING_PORT } from '../NetworkPrintingTypes';
import { InvalidPrinterIPException, MissingZplDataException, InvalidPortException, NetworkPrintingUnknownException } from '../NetworkPrintingExceptions';
import { VelocityBaseAdapter } from '../../shared/VelocityBaseAdapter';

/**
 * Velocity browser-specific implementation of network printing adapter.
 * Uses wls: URL scheme to invoke Velocity browser functions for network printing.
 */
export class VelocityNetworkPrintingAdapter extends AbstractNetworkPrintingAdapter {
    private velocityAdapter = new (class extends VelocityBaseAdapter {
        protected createTimeoutError(): Error {
            return new NetworkPrintingUnknownException({ message: 'Velocity call timed out' });
        }
    })();

    /**
     * Prints ZPL data to a network printer using Velocity browser functions.
     *
     * @param config - Configuration object containing printer IP, ZPL data, and optional port
     * @returns Promise that resolves when the print operation completes successfully
     */
    public async print(config: INetworkPrintingConfig): Promise<any> {
        await this.validateConfig(config);

        const port = config.port || DEFAULT_NETWORK_PRINTING_PORT;

        // Encode non-ASCII characters (e.g. ñ, Japanese) to ZPL hex-encoded UTF-8.
        // This produces pure ASCII that passes safely through the wls: URL scheme,
        // and the printer natively decodes the hex bytes back to characters.
        const encodedZpl = VelocityBaseAdapter.encodeNonAsciiForZpl(config.zplData);

        return this.velocityAdapter.callVelocityFunction('printViaIP', encodedZpl, config.printerIP, port).then((result: any) => {
            /**
             * Velocity print function returns a response in the format
             * {result: -1, extra: [{name: "message", value: "PRINT_SUCCESS", type: "string"}]}
             *
             * negative number means success, positive or zero means failure.
             */
            if (result?.result < 0) {
                return Promise.resolve();
            }
            return Promise.reject(new NetworkPrintingUnknownException({ message: JSON.stringify(result) }));
        });
    }

    /**
     * Validates the configuration parameters and returns rejected promises for invalid inputs.
     *
     * @param config - Configuration to validate
     * @returns Promise that resolves if validation passes, or rejects with appropriate exception
     */
    private validateConfig(config: INetworkPrintingConfig): Promise<void> {
        if (!NetworkPrintingValidation.isValidIPAddress(config.printerIP)) {
            return Promise.reject(
                new InvalidPrinterIPException({
                    message: `Invalid printer IP address: ${config.printerIP}`,
                }),
            );
        }

        if (!NetworkPrintingValidation.isValidZplData(config.zplData)) {
            return Promise.reject(
                new MissingZplDataException({
                    message: 'ZPL data is required and cannot be empty',
                }),
            );
        }

        if (config.port !== undefined && !NetworkPrintingValidation.isValidPort(config.port)) {
            return Promise.reject(
                new InvalidPortException({
                    message: `Invalid port number: ${config.port}. Port must be between 1 and 65535`,
                }),
            );
        }

        return Promise.resolve();
    }

    /**
     * Queries the DPI of a network printer using Velocity browser functions.
     *
     * @param config - Configuration object containing printer IP and optional port
     * @returns Promise that resolves with the printer DPI result
     */
    public getPrinterDPI(config: IGetPrinterDPIConfig): Promise<IGetPrinterDPIResult> {
        if (!NetworkPrintingValidation.isValidIPAddress(config.printerIP)) {
            return Promise.reject(new InvalidPrinterIPException({ message: `Invalid printer IP address: ${config.printerIP}` }));
        }

        if (config.port !== undefined && !NetworkPrintingValidation.isValidPort(config.port)) {
            return Promise.reject(new InvalidPortException({ message: `Invalid port number: ${config.port}. Port must be between 1 and 65535` }));
        }

        const port = config.port || DEFAULT_NETWORK_PRINTING_PORT;

        return this.velocityAdapter.callVelocityFunction('getPrinterDPIViaIP', config.printerIP, port).then((result: any) => {
            if (result?.result < 0) {
                const dpiValue = result?.extra?.[0]?.value;
                const dpi = parseInt(dpiValue, 10);
                if (isNaN(dpi)) {
                    return Promise.reject(new NetworkPrintingUnknownException({ message: `Invalid DPI value: ${dpiValue}` }));
                }
                return { dpi };
            }
            return Promise.reject(new NetworkPrintingUnknownException({ message: JSON.stringify(result) }));
        });
    }
}
